import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Box,
  TextareaAutosize,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import warningIcon from '../../../assets/svgs/warning.svg';
import SelectDropdown from '../../../components/SelectDropdown/SelectDropdown';

interface ResetConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

const ResetConfirmationModal = ({
  open,
  onClose,
  onConfirm,
}: ResetConfirmationModalProps) => {
  const [atFault, setAtFault] = useState('');
  const [faultAttribution, setFaultAttribution] = useState('MANNA');
  const [resetReason, setResetReason] = useState('');
  const [hasValidationError, setHasValidationError] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setAtFault('');
      setFaultAttribution('MANNA');
      setResetReason('');
      setHasValidationError(false);
    }
  }, [open]);

  const handleConfirm = () => {
    // Validate that a category is selected
    if (!atFault || atFault === '') {
      setHasValidationError(true);
      return;
    }

    const formattedReason = `Fault: ${faultAttribution} - ${atFault}${resetReason ? ` - ${resetReason}` : ''}`;
    onConfirm(formattedReason);
    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  const faultOptions = [
    {
      displayText: 'Select a category',
      value: ''
    },
    {
      displayText: 'Car in Drive',
      value: 'CAR_IN_DRIVE'
    },
    {
      displayText: 'Merchant Issue',
      value: 'MERCHANT_ISSUE'
    },
    {
      displayText: 'Manna Ops Issue',
      value: 'MANNA_ISSUE_OPS'
    },
    {
      displayText: 'Software Issue',
      value: 'SOFTWARE_ISSUE'
    },
    {
      displayText: 'Weather (Wind - Over Limits)',
      value: 'WEATHER_WIND_OVER_LIMITS'
    },
    {
      displayText: 'Weather (Wind - Range)',
      value: 'WEATHER_WIND_RANGE'
    },
    {
      displayText: 'Weather (Rain)',
      value: 'WEATHER_RAIN'
    },
    {
      displayText: 'Bag Drop',
      value: 'BAG_DROP'
    },
    {
      displayText: 'Too long at base',
      value: 'TOO_LONG_AT_BASE'
    },
    {
      displayText: 'Unsuitable Droppoint / No Suitable Pin',
      value: 'UNSUITABLE_DROPPOINT'
    },
    {
      displayText: 'Obstacle Under Droppoint',
      value: 'OBSTACLE_UNDER_DROPPOINT'
    },
    {
      displayText: 'Overweight order',
      value: 'OVERWEIGHT'
    },
    {
      displayText: 'Other',
      value: 'OTHER'
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="reset-confirmation-dialog-title"
      PaperProps={{
        style: styles.dialogPaper
      }}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        style={styles.closeButton}
      >
        <CloseIcon />
      </IconButton>
      
      <DialogContent style={styles.dialogContent}>
        <div style={styles.warningIcon}>
          <img src={warningIcon} alt="Warning" style={styles.warningImage} />
        </div>
        
        <Box style={styles.contentContainer}>
          <Typography
            id="reset-confirmation-dialog-title"
            style={styles.title}
          >
            Reset Order
          </Typography>
          
          <Typography style={styles.description}>
            Please select the reason for resetting this order and provide any additional details.
          </Typography>

          <div style={styles.formContainer}>
            <div style={styles.fieldContainer}>
              <FormControl component="fieldset">
                <FormLabel component="legend" style={styles.fieldLabel}>Who is at fault?</FormLabel>
                <RadioGroup
                  row
                  aria-label="fault-attribution"
                  name="fault-attribution"
                  value={faultAttribution}
                  onChange={(e) => setFaultAttribution(e.target.value)}
                >
                  <FormControlLabel 
                    value="MANNA" 
                    control={<Radio />} 
                    label={<Typography style={styles.radioLabel}>Manna</Typography>} 
                  />
                  <FormControlLabel 
                    value="VENDOR" 
                    control={<Radio />} 
                    label={<Typography style={styles.radioLabel}>Vendor</Typography>} 
                  />
                  <FormControlLabel 
                    value="CUSTOMER" 
                    control={<Radio />} 
                    label={<Typography style={styles.radioLabel}>Customer</Typography>} 
                  />
                </RadioGroup>
              </FormControl>
            </div>

            <div style={styles.fieldContainer}>
              <div className={hasValidationError ? 'dropdown-error' : ''}>
                <style>{`
                  .dropdown-error .relative.mt-2 button {
                    border-color: #dc2626 !important;
                    border-width: 2px !important;
                    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.2) !important;
                  }
                  .dropdown-placeholder button span:first-child {
                    color: #9ca3af !important;
                    font-style: italic;
                  }
                `}</style>
                <div className={atFault === '' ? 'dropdown-placeholder' : ''}>
                  <SelectDropdown
                    label='Category'
                    value={atFault}
                    onChange={(value: string) => {
                      setAtFault(value);
                      // Clear validation error when user makes a selection
                      if (hasValidationError && value !== '') {
                        setHasValidationError(false);
                      }
                    }}
                    options={faultOptions}
                  />
                </div>
              </div>
            </div>

            <div style={styles.fieldContainer}>
              <Typography style={styles.fieldLabel}>Additional Notes</Typography>
              <TextareaAutosize
                style={styles.textarea}
                maxRows={6}
                minRows={3}
                value={resetReason}
                onChange={(evt) => setResetReason(evt.target.value)}
                placeholder="Enter any additional details..."
              />
            </div>
          </div>
        </Box>
        
        <div style={styles.buttonContainer}>
          <Button
            onClick={handleClose}
            style={styles.cancelButton}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            style={styles.actionButton}
            variant="contained"
          >
            Reset Order
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const styles = {
  dialogPaper: {
    borderRadius: 12,
    padding: 0,
    maxWidth: 500,
    width: '100%',
  },
  dialogContent: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  closeButton: {
    position: 'absolute' as const,
    right: 24,
    top: 24,
    width: 24,
    height: 24,
    color: '#6b7280',
  },
  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    marginBottom: 32,
  },
  warningIcon: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 32,
    flexShrink: 0,
  },
  warningImage: {
    width: 24,
    height: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 8,
    color: '#101828',
    fontFamily: 'Outfit',
  },
  description: {
    fontSize: 16,
    color: '#475467',
    fontWeight: 400,
    lineHeight: 1.5,
    fontFamily: 'Outfit',
    marginBottom: 24,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: '#374151',
    fontFamily: 'Outfit',
  },
  radioLabel: {
    fontSize: 14,
    fontFamily: 'Outfit',
    color: '#374151',
  },
  textarea: {
    padding: 12,
    width: '100%',
    fontFamily: 'Arial',
    fontSize: 14,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    resize: 'vertical' as const,
    outline: 'none',
    '&:focus': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  buttonContainer: {
    display: 'flex',
    gap: 12,
    width: '100%',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    width: 170,
    height: 44,
    borderRadius: 8,
    padding: '10px 18px',
    textTransform: 'none' as const,
    fontSize: 16,
    fontWeight: 500,
    color: '#111827',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    fontFamily: 'Outfit',
    '&:hover': {
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
    },
  },
  actionButton: {
    width: 170,
    height: 44,
    borderRadius: 8,
    padding: '10px 18px',
    textTransform: 'none' as const,
    fontSize: 16,
    fontWeight: 500,
    color: 'white',
    backgroundColor: '#3b82f6',
    fontFamily: 'Outfit',
    '&:hover': {
      backgroundColor: '#2563eb',
    },
  },
};

export default ResetConfirmationModal;
